import { Location } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import {
  modalTransition,
  modalTransitionPresets,
} from '@app/finxone-web-frontend/app/Modules/zones/parent-container/modal-transitions';
import { CloseModalPopupAction } from '@app/finxone-web-frontend/app/lib/actions/page-context.action';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { DeviceTypeService } from '@app/finxone-web-frontend/app/lib/services/device-type/device-type.service';
import { PaymentGatewayConfig } from '@app/finxone-web-frontend/app/lib/state/account.state';
import { ProjectSettingsState } from '@app/finxone-web-frontend/app/lib/state/project-settings.state';
import { RoleState, RoleStateModel } from '@app/finxone-web-frontend/app/lib/state/role.state';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  APP_ZONES,
  ActiveDevice,
  BaseCssProperties,
  ModalConfiguration,
  SupportedDevice,
  UiPageConfig,
  UiZoneWidgetAttributeConfig,
  baseCssConfigStyle,
  cssToNgStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import {
  Observable,
  Subject,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { getModalConfigStyle, stripModalFromLocation } from '../../modal-utils';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-modal',
  templateUrl: './page-as-modal.component.html',
  styleUrl: './page-as-modal.component.scss',
  animations: [modalTransition],
})
export class PageAsModalComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();

  @Input() device: SupportedDevice | null;
  @Input() paymentProviderConfig: PaymentGatewayConfig;

  protected readonly modalTransitionPresets = modalTransitionPresets;

  public menuTypeForDesktop = 'modal';
  public closeIconName = 'close';
  public zoneUrlName = '';
  public pageUrlName = '';
  public isWorkflow: boolean;
  public currentPageDetail: UiPageConfig;
  public closeExternalClick: boolean | undefined;
  public closeAction: boolean | undefined;
  public iconStyle: baseCssConfigStyle;
  public iconContentStyle: baseCssConfigStyle;
  public backdropColor: string | undefined;
  public modalContentStyle: baseCssConfigStyle;
  public modalConfigOuterStyle: baseCssConfigStyle;
  public modalConfigPaddingStyle: baseCssConfigStyle;
  public bottomPadding = '0px';
  public topPadding = '0px';
  public modalTransitionPreset: string;

  private addBottomSafeAreaPadding = true;
  private addTopSafeAreaPadding = true;

  @Select(RoleState.getRole) role$!: Observable<RoleStateModel>;

  public modalState = 'in';
  private readonly deviceType: ActiveDevice;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly configService: ConfigService,
    private readonly location: Location,
    private readonly store: Store,
    private readonly cdr: ChangeDetectorRef,
    private readonly deviceTypeService: DeviceTypeService,
  ) {
    this.isWorkflow = configService.getActiveConfigType().isWorkflow;
    if (this.isWorkflow) {
      this.onGetRouteParams();
    }
    this.deviceType = this.deviceTypeService.deviceType as ActiveDevice;
    // use mobile transition for tablet until we have tablet transitions
    const deviceType =
      this.deviceTypeService.deviceType === ActiveDevice.TABLET ? ActiveDevice.MOBILE : this.deviceType;
    this.modalTransitionPreset =
      this.store.selectSnapshot(ProjectSettingsState.getProjectSettings)?.uiSettings?.[deviceType]
        ?.modalTransition || '';
  }

  private onGetRouteParams() {
    this.route?.paramMap
      ?.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((params: ParamMap) => {
        const zoneUrlName = params.get('zone');
        let pageUrlName = params.get('modal-page') ? params.get('modal-page') : params.get('page');
        const modalPageUrlName = params.get('modal-page');
        if (modalPageUrlName) {
          pageUrlName = modalPageUrlName;
        }

        if (zoneUrlName === null && pageUrlName === null) {
          throw Error('No zone or page specified via path or input');
        } else {
          this.zoneUrlName = zoneUrlName ?? '';
          this.pageUrlName = pageUrlName ?? '';
        }
      });
  }

  ngOnInit(): void {
    const bodyElement = document.getElementById('web-app');
    if (bodyElement) {
      bodyElement.classList.add('overflow-hidden');
    }
    this.onGetRouterEvents();
    this.onGetCurrentDeviceType();
    this.onGetCurrentPageConfigByPageUrl();
  }

  ngOnDestroy(): void {
    const bodyElement = document.getElementById('web-app');
    if (bodyElement) {
      bodyElement.classList.remove('overflow-hidden');
    }
  }

  ngAfterViewInit() {
    this.addSafeAreaModalPadding(this.modalConfigOuterStyle)
      .pipe(take(1))
      .subscribe((result) => {
        this.modalConfigOuterStyle = result;
      });
  }

  ngOnChanges() {
    this.addSafeAreaModalPadding(this.modalConfigOuterStyle)
      .pipe(take(1))
      .subscribe((result) => {
        this.modalConfigOuterStyle = result;
      });
  }

  private onGetRouterEvents() {
    if (!this.isWorkflow) {
      this.router.events
        ?.pipe(distinctUntilChanged(), debounceTime(100), takeUntil(this.destroy$))
        .subscribe(() => {
          this.setModalState('active');
          this.onGetCurrentPageConfigByPageUrl();
        });
    }
  }

  private setModalState(state: 'in' | 'active') {
    switch (state) {
      case 'in':
        setTimeout(() => {
          this.modalState = state;
          this.cdr.detectChanges();
        });
        break;
      case 'active':
        this.modalState = state;
        this.cdr.detectChanges();
        break;
    }
  }

  private onGetCurrentPageConfigByPageUrl(): void {
    if (!this.isWorkflow) {
      // to get the modal name from the string
      this.pageUrlName = location.pathname?.split('/').pop()?.replace(')', '') as string;
      this.route?.children?.[0]?.paramMap?.pipe(takeUntil(this.destroy$)).subscribe((params) => {
        this.handleZoneParams(params?.get('zone'));
      });
    }
    this.configService
      .getPageUrlPage(this.pageUrlName)
      .pipe(
        takeUntil(this.destroy$),
        switchMap((result) => {
          if (!result) {
            return [];
          }

          this.setModalState('in');
          this.currentPageDetail = result;
          const pageConfiguration = this.currentPageDetail.pageConfiguration;
          const modalConfiguration = pageConfiguration?.modalConfiguration;

          this.setModalConfigurations(modalConfiguration);

          const outerStyle = this.setModalStylesFromConfigAndGetOuterStyles(modalConfiguration);

          return this.addSafeAreaModalPadding(outerStyle).pipe(take(1));
        }),
      )
      .subscribe((result) => {
        if (result) {
          this.modalConfigOuterStyle = result as baseCssConfigStyle;
        }
      });
  }

  private handleZoneParams(currentZone: string | null): void {
    if (currentZone && currentZone === APP_ZONES.ONBOARDING) {
      const match = location.pathname.match(/\(modal:([^)]+)\)/);
      if (match && match[1]) {
        const segments = match[1].split('/');
        this.pageUrlName = segments[2];
      }
    }
  }

  private setModalStylesFromConfigAndGetOuterStyles(
    modalConfiguration: ModalConfiguration | undefined,
  ): baseCssConfigStyle {
    const { contentStyle, marginStyle, paddingStyle, closeIconContentStyle } = getModalConfigStyle(
      this.currentPageDetail,
      this.device as SupportedDevice,
    );

    this.iconContentStyle = closeIconContentStyle;

    const outerStyle: Record<string, string | undefined> = modalConfiguration?.cssEnabled
      ? {
          ...marginStyle,
          ...cssToNgStyle(modalConfiguration?.css ?? ''),
        }
      : {
          ...marginStyle,
          position: 'fixed',
          top: '15%',
          left: '10px',
          right: '10px',
          display: 'flex',
          'justify-content': 'center',
        };

    this.modalConfigPaddingStyle = paddingStyle as baseCssConfigStyle;

    this.modalContentStyle = this.getModalContentStyles(
      modalConfiguration?.cssEnabled ?? false,
      contentStyle,
      marginStyle,
    );

    return outerStyle;
  }

  private getModalContentStyles(
    isCustomCssEnabled: boolean,
    baseStyles: baseCssConfigStyle,
    marginStyles: {
      marginTop: string | undefined;
      marginBottom: string | undefined;
      marginLeft: string | undefined;
      marginRight: string | undefined;
    },
  ): baseCssConfigStyle {
    return isCustomCssEnabled
      ? {
          ...baseStyles,
          ...this.modalConfigPaddingStyle,
          width: '100%',
          height: '100%',
          bottom: marginStyles?.marginBottom,
        }
      : ({
          'background-color': 'var(--color-background)',
          'padding-top': '2rem',
          'padding-bottom': '2rem',
          position: 'relative',
          overflow: 'auto',
          width: '400px',
          height: '500px',
          bottom: marginStyles?.marginBottom,
          ...baseStyles,
          ...this.modalConfigPaddingStyle,
        } as baseCssConfigStyle);
  }

  private setModalConfigurations(modalConfiguration: ModalConfiguration | undefined): void {
    this.backdropColor = modalConfiguration?.backDropColor;
    this.closeExternalClick = modalConfiguration?.closeExternalClick ?? false;
    this.closeAction = modalConfiguration?.closeAction ?? true;
    this.closeIconName = modalConfiguration?.closeIconName || 'close';
    this.addBottomSafeAreaPadding = modalConfiguration?.addBottomSafeAreaPadding ?? true;
    this.addTopSafeAreaPadding = modalConfiguration?.addTopSafeAreaPadding ?? true;

    this.iconStyle = this.configService.getSpecificBaseCssStyle(
      {
        css_config: {
          iconColor: modalConfiguration?.closeIconColour ?? '',
          iconSize: modalConfiguration?.closeIconSize ?? 32,
          iconUnit: modalConfiguration?.closeIconUnit ?? 'px',
        },
      } as UiZoneWidgetAttributeConfig,
      [BaseCssProperties.ICON_COLOR, BaseCssProperties.ICON_SIZE],
    );
  }

  private onGetCurrentDeviceType() {
    this.role$.pipe(takeUntil(this.destroy$)).subscribe((roles) => {
      this.device = roles?.device;
    });
  }

  public clickOutSideModal() {
    if (this.closeExternalClick) {
      this.closeModal();
    }
  }

  public closeModal() {
    if (this.isWorkflow) {
      return;
    }
    const isPrevModal = this.route.snapshot?.queryParams?.['isModal'] === 'true';

    if (isPrevModal || this.route.snapshot?.queryParams?.['isModal'] == null) {
      // Navigate back and dispatch the appropriate modal close action
      this.location.back();
    } else {
      // Navigate to the stripped modal location
      this.router.navigate([stripModalFromLocation()], { replaceUrl: true });
    }
    // Dispatch the close action
    this.store.dispatch(new CloseModalPopupAction(isPrevModal));
  }

  private addSafeAreaModalPadding(outerStyle: Record<string, string>): Observable<Record<string, string>> {
    const modalElement = document.getElementsByClassName('page-as-modal')[0];
    const rect = modalElement.getBoundingClientRect();

    // This is the in app padding needed for the safe area on mobile devices
    return this.configService.getSafePadding().pipe(
      take(1),
      map((padding) => {
        if (!padding.isIOS) {
          return outerStyle;
        }

        const { topPadding, bottomPadding } = padding;
        let updatedStyle = outerStyle;

        updatedStyle = this.calculateBottomPadding(updatedStyle, bottomPadding, rect);
        updatedStyle = this.calculateTopPadding(updatedStyle, topPadding, rect);

        return updatedStyle;
      }),
    );
  }

  private calculateBottomPadding(
    style: Record<string, string>,
    bottomPadding: number,
    rect: DOMRect,
  ): Record<string, string> {
    const safeAreaBottomHeight = window.innerHeight - bottomPadding;
    if (rect.bottom <= safeAreaBottomHeight || !this.addBottomSafeAreaPadding) {
      return style;
    }

    this.bottomPadding = `${bottomPadding}px`;
    if (!style) {
      return style;
    }

    const updatedStyle = structuredClone(style);
    if (updatedStyle['paddingBottom'] === undefined) updatedStyle['paddingBottom'] = this.bottomPadding;
    else updatedStyle['paddingBottom'] = `calc(${updatedStyle['paddingBottom']} + ${this.bottomPadding})`;

    // mark for redraw as we are modifying the padding
    this.cdr.detectChanges();
    return updatedStyle;
  }

  private calculateTopPadding(
    style: Record<string, string>,
    topPadding: number,
    rect: DOMRect,
  ): Record<string, string> {
    const safeAreaTopHeight = topPadding;
    if (rect.top >= safeAreaTopHeight || !this.addTopSafeAreaPadding) {
      return style;
    }

    this.topPadding = `${topPadding}px`;
    if (!style) {
      return style;
    }

    const updatedStyle = structuredClone(style);
    if (updatedStyle['paddingTop'] === undefined) {
      updatedStyle['paddingTop'] = this.topPadding;
    } else {
      updatedStyle['paddingTop'] = `calc(${updatedStyle['paddingTop']} + ${this.topPadding})`;
    }

    // mark for redraw as we are modifying the padding
    this.cdr.detectChanges();
    return updatedStyle;
  }
}
